// In-memory stand-in for the personnel contract while VITE_CREDENTIALING_MOCKS is on.
// Fictional people only. The full date of birth and ID number stay in this module
// and leave it only through reveal(), the same way the real API is expected to work.
import {
  CreatePersonnelRequest,
  DuplicateCheckRequest,
  DuplicateMatch,
  ImageUploadResponse,
  PageResponse,
  PersonnelListItem,
  PersonnelListRequest,
  PersonnelProfile,
  PersonnelStatus,
  RevealRequest,
  RevealResponse,
  UpdatePersonnelRequest,
} from '@src/types/personnel';
import { mockApiError } from '../credentialingContract';
import { recordStatusChange } from './complianceMocks';
import { revealRecordNumber } from './credentialMocks';
import { addNote } from './notesMocks';

type MockPerson = Omit<
  PersonnelProfile,
  'age' | 'dobMasked' | 'identificationNumberMasked' | 'status'
> & {
  // the mocks keep the contract's label
  status: PersonnelStatus;
  dob: string;
  identificationNumber: string | null;
  compliantPayers: number;
  expiringSoon: boolean;
};

const LINKED_PAYERS = 3;

const seed = (
  id: number,
  firstName: string,
  lastName: string,
  status: PersonnelStatus,
  phone: string,
  compliantPayers: number,
  dob: string,
  identificationNumber: string | null,
  expiringSoon = false
): MockPerson => ({
  id,
  firstName,
  lastName,
  status,
  phone,
  compliantPayers,
  dob,
  identificationNumber,
  expiringSoon,
  email: `${firstName}.${lastName}@example.com`.toLowerCase(),
  effectiveDate: compliantPayers ? '03/02/2026' : null,
  expirationDate: null,
  profilePictureUrl: null,
});

// prettier-ignore
let people: Array<MockPerson> = [
  seed(1, 'Marcus', 'Allen', 'Active', '(323) 555-0189', 3, '02/11/1985', 'D1180312'),
  seed(2, 'Olivia', 'Brooks', 'Terminated', '(747) 555-0136', 0, '07/23/1990', 'D5520936'),
  seed(3, 'James', 'Carter', 'Active', '(818) 555-0193', 3, '11/02/1979', 'D7741193'),
  seed(4, 'Daniel', 'Kim', 'Suspended', '(310) 555-0128', 3, '05/30/1988', 'D3390128'),
  seed(5, 'Maria', 'Lopez', 'Active', '(626) 555-0142', 3, '04/18/1992', 'D0084821', true),
  seed(6, 'Sofia', 'Martinez', 'Inactive', '(562) 555-0164', 0, '09/14/1995', null),
  seed(7, 'Kevin', 'Nguyen', 'Active', '(909) 555-0115', 2, '01/06/1983', 'D6610115', true),
  seed(8, 'Priya', 'Patel', 'Active', '(657) 555-0172', 2, '12/19/1991', 'D2230172'),
  seed(9, 'Aisha', 'Rahman', 'Active', '(213) 555-0177', 1, '03/27/1987', 'D9940177'),
  seed(10, 'Ethan', 'Walker', 'Active', '(424) 555-0150', 1, '08/08/1993', 'D4450150'),
];

// enough extra rows to page through, like the 48 entries of the design
const EXTRA_FIRST = [
  'Ava',
  'Liam',
  'Noah',
  'Emma',
  'Mia',
  'Lucas',
  'Zoe',
  'Omar',
];
const EXTRA_LAST = ['Reyes', 'Shah', 'Turner', 'Young', 'Diaz', 'Hughes'];
const EXTRA_STATUSES: Array<PersonnelStatus> = [
  'Active',
  'Active',
  'Inactive',
  'Suspended',
];

people = [
  ...people,
  ...EXTRA_LAST.flatMap((last, lastIndex) =>
    EXTRA_FIRST.slice(0, 6 + (lastIndex % 3)).map((first, firstIndex) => {
      const id = 11 + lastIndex * 8 + firstIndex;
      const suffix = String(1000 + id).slice(-4);

      return seed(
        id,
        first,
        last,
        EXTRA_STATUSES[id % EXTRA_STATUSES.length],
        `(213) 555-${suffix}`,
        id % (LINKED_PAYERS + 1),
        `0${(id % 9) + 1}/1${id % 9}/19${80 + (id % 19)}`,
        `D77${suffix}0`,
        id % 5 === 0
      );
    })
  ),
];

const nextId = () => Math.max(0, ...people.map(({ id }) => id)) + 1;

const ageOf = (dob: string): number => {
  const [month, day, year] = dob.split('/').map(Number);
  const today = new Date();
  const hadBirthday =
    today.getMonth() + 1 > month ||
    (today.getMonth() + 1 === month && today.getDate() >= day);

  return today.getFullYear() - year - (hadBirthday ? 0 : 1);
};

const maskDob = (dob: string) => `**/**/${dob.slice(-4)}`;

const maskNumber = (value: string | null) =>
  value ? `***${value.slice(-4)}` : null;

const findPerson = (id: number): MockPerson => {
  const person = people.find((item) => item.id === id);
  if (!person) throw mockApiError(404, 'Personnel not found');
  return person;
};

const toProfile = ({
  dob,
  identificationNumber,
  compliantPayers: _compliantPayers,
  expiringSoon: _expiringSoon,
  ...person
}: MockPerson): PersonnelProfile => ({
  ...person,
  age: ageOf(dob),
  dobMasked: maskDob(dob),
  identificationNumberMasked: maskNumber(identificationNumber),
});

const toListItem = (person: MockPerson): PersonnelListItem => ({
  id: person.id,
  firstName: person.firstName,
  lastName: person.lastName,
  status: person.status,
  phone: person.phone,
  compliantPayers: person.compliantPayers,
  linkedPayers: LINKED_PAYERS,
});

const sameName = (left: string, right: string) =>
  left.trim().toLowerCase() === right.trim().toLowerCase();

const TEXT_FILTER_KEYS = ['firstName', 'lastName', 'phone'] as const;

// reads the table Filter popup body the way the backend is expected to
const matchesFilter = (
  person: MockPerson,
  key: string,
  { operator, value }: { operator: string; value: unknown }
): boolean => {
  if (TEXT_FILTER_KEYS.includes(key as (typeof TEXT_FILTER_KEYS)[number])) {
    const field = String(person[key as keyof MockPerson] ?? '').toLowerCase();
    const wanted = String(value ?? '').toLowerCase();
    return operator === 'equals' ? field === wanted : field.includes(wanted);
  }

  switch (key) {
    case 'status':
      return person.status === value;
    case 'compliance':
      return value === 'Compliant'
        ? person.compliantPayers > 0
        : person.compliantPayers < LINKED_PAYERS;
    case 'expiringSoon':
      return person.expiringSoon === value;
    default:
      return true;
  }
};

export const personnelMocks = {
  list: ({ params, body = {} }: PersonnelListRequest) => {
    const page = params?.page ?? 0;
    const size = params?.size ?? 10;
    const sortBy = (params?.sortBy ?? 'lastName') as keyof PersonnelListItem;
    const direction = params?.sortDir === 'desc' ? -1 : 1;

    const filtered = people
      .filter((person) =>
        Object.entries(body).every(([key, filter]) =>
          matchesFilter(person, key, filter)
        )
      )
      .map(toListItem)
      .sort(
        (left, right) =>
          String(left[sortBy]).localeCompare(String(right[sortBy])) * direction
      );

    const response: PageResponse<PersonnelListItem> = {
      content: filtered.slice(page * size, page * size + size),
      page,
      size,
      totalElements: filtered.length,
      totalPages: Math.max(1, Math.ceil(filtered.length / size)),
    };

    return response;
  },

  checkDuplicate: ({
    firstName,
    lastName,
    dob,
  }: DuplicateCheckRequest): Array<DuplicateMatch> =>
    people
      .filter(
        (person) =>
          sameName(person.firstName, firstName) &&
          sameName(person.lastName, lastName) &&
          person.dob === dob
      )
      .map(({ id, firstName: first, lastName: last }) => ({
        id,
        firstName: first,
        lastName: last,
      })),

  getProfile: ({ id }: { id: number }) => toProfile(findPerson(id)),

  create: (input: CreatePersonnelRequest) => {
    const person: MockPerson = {
      ...input,
      id: nextId(),
      email: input.email ?? null,
      profilePictureUrl: input.profilePictureUrl ?? null,
      identificationNumber: null,
      effectiveDate: null,
      expirationDate: null,
      compliantPayers: 0,
      expiringSoon: false,
    };

    people = [...people, person];
    return toProfile(person);
  },

  update: ({ id, changes, note }: UpdatePersonnelRequest) => {
    if (!note?.text || note.text.trim().length < 15) {
      throw mockApiError(400, 'A note of at least 15 characters is required');
    }

    const current = findPerson(id);
    const updated = { ...current, ...changes };
    if (changes.status && changes.status !== current.status) {
      recordStatusChange(id, current.status, changes.status);
    }
    // the required note becomes a contractor note on the person
    addNote(id, note);
    people = people.map((person) => (person.id === id ? updated : person));
    return toProfile(updated);
  },

  // keeps the picked image in the browser; the real call stores it
  uploadPicture: ({ body }: { body: FormData }): ImageUploadResponse => {
    const file = body.get('file');
    if (!(file instanceof File)) throw mockApiError(400, 'No file attached');
    return { success: true, message: URL.createObjectURL(file) };
  },

  reveal: ({ personnelId, field, recordId }: RevealRequest): RevealResponse => {
    // the number on one identification record, older or expired ones too
    if (recordId) return { value: revealRecordNumber(recordId) };

    const person = findPerson(personnelId);
    const value = field === 'dob' ? person.dob : person.identificationNumber;

    if (!value) throw mockApiError(404, 'Nothing to reveal');
    return { value };
  },
};
